import { forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import qLogo from "../../../assets/q.png";
import { ACTION_LABELS } from "../sceneCopy";

/**
 * Shared full-bleed scene frame.
 * Animation targets: root, .cinematic-scene__media, .cinematic-scene__copy
 */
const SceneFrame = forwardRef(function SceneFrame(
  {
    sceneId,
    imageSrc,
    imageSrcSet,
    imageSizes = "100vw",
    copy,
    language = "en",
    isArabic = false,
    priority = false,
  },
  ref
) {
  const navigate = useNavigate();
  const labels = ACTION_LABELS[language === "ar" ? "ar" : "en"];

  return (
    <div
      ref={ref}
      className={`cinematic-scene cinematic-scene--${sceneId}`}
      data-scene={sceneId}
      aria-hidden={sceneId === 1 ? undefined : true}
    >
      <div className="cinematic-scene__media-wrap">
        <img
          className="cinematic-scene__media"
          src={imageSrc}
          srcSet={imageSrcSet}
          alt=""
          draggable={false}
          loading={priority ? "eager" : "lazy"}
          decoding={priority ? "sync" : "async"}
          fetchpriority={priority ? "high" : "auto"}
          sizes={imageSizes}
        />
        <div className="cinematic-scene__shade" aria-hidden="true" />
      </div>

      <div
        className={`cinematic-scene__copy ${
          copy.align === "right" ? "cinematic-scene__copy--right" : ""
        } ${copy.align === "left" ? "cinematic-scene__copy--left" : ""} ${
          copy.layout === "office-card" ? "cinematic-scene__copy--office-card" : ""
        } ${copy.layout === "finale" ? "cinematic-scene__copy--finale" : ""} ${
          isArabic ? "cinematic-scene__copy--rtl font-arabic2" : "font-english2"
        }`}
      >
        {copy.layout === "finale" && (
          <div className="cinematic-scene__finale">
            {(copy.showLogo || copy.brand) && (
              <div
                className={`cinematic-scene__finale-brand${
                  !copy.brand ? " cinematic-scene__finale-brand--logo-only" : ""
                }`}
              >
                <img
                  className="cinematic-scene__finale-logo"
                  src={qLogo}
                  alt=""
                  draggable={false}
                />
                {copy.brand && (
                  <span className="cinematic-scene__finale-name">{copy.brand}</span>
                )}
              </div>
            )}
            {copy.title && (
              <h2 className="cinematic-scene__finale-headline">{copy.title}</h2>
            )}
            <div className="cinematic-scene__finale-rule" aria-hidden="true" />
            {copy.description && (
              <p className="cinematic-scene__finale-tagline">{copy.description}</p>
            )}
          </div>
        )}
        {copy.brand && copy.layout !== "finale" && (
          <p className="cinematic-scene__brand">{copy.brand}</p>
        )}
        {copy.label && <p className="cinematic-scene__label">{copy.label}</p>}
        {copy.eyebrow && <p className="cinematic-scene__eyebrow">{copy.eyebrow}</p>}
        {copy.layout !== "finale" && sceneId === 1 && copy.titleStyle === "poster" ? (
          <div className="cinematic-scene__poster-slot">
            {copy.title && (
              <h2 className="cinematic-scene__title cinematic-scene__title--poster">
                {copy.title}
              </h2>
            )}
            {copy.description && (
              <p className="cinematic-scene__description">{copy.description}</p>
            )}
          </div>
        ) : copy.layout !== "finale" ? (
          <>
            {copy.title && (
              <h2
                className={`cinematic-scene__title${
                  copy.titleStyle === "poster" ? " cinematic-scene__title--poster" : ""
                }`}
              >
                {copy.title}
              </h2>
            )}
            {copy.description && (
              <p className="cinematic-scene__description">{copy.description}</p>
            )}
          </>
        ) : null}
        {copy.showActions && (
          <div className="cinematic-scene__actions">
            <p className="cinematic-scene__actions-heading">{labels.demoHeader}</p>
            <div className="cinematic-scene__actions-row">
              <button
                type="button"
                className="cinematic-scene__btn cinematic-scene__btn--secondary"
                onClick={() =>
                  navigate("/demo-importer", { state: { userType: "importer" } })
                }
              >
                {labels.secondary}
              </button>
              <button
                type="button"
                className="cinematic-scene__btn cinematic-scene__btn--primary"
                onClick={() =>
                  navigate("/demo", { state: { userType: "service_provider" } })
                }
              >
                {labels.primary}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default SceneFrame;
